import React from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  IconButton,
  Divider,
  useToast,
  Flex,
  Heading,
  Badge,
  useColorModeValue,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { FiTrash2, FiMinus, FiPlus } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import type { Product } from "../types";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const Cart: React.FC = () => {
  const { items: cart, removeFromCart, updateQuantity } = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handleQuantityChange = (productId: number, value: number) => {
    updateQuantity(productId, value);
  };

  const handleRemove = (productId: number) => {
    removeFromCart(productId);
    toast({
      title: "Item removed",
      description: "The item has been removed from your cart",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const calculateTotal = () => {
    return cart.reduce((total: number, item: Product) => total + item.price * item.quantity, 0);
  };

  if (cart.length === 0) {
    return (
      <Box py={20}>
        <Container maxW="container.md">
          <VStack spacing={8} textAlign="center">
            <Heading size="2xl" color="purple.700">
              Your Cart is Empty
            </Heading>
            <Text fontSize="xl" color="gray.600">
              Discover our collection of luxury fragrances
            </Text>
            <Button
              colorScheme="purple"
              size="lg"
              onClick={() => navigate("/categories")}
              leftIcon={<FiPlus />}
            >
              Continue Shopping
            </Button>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box py={12}>
      <Container maxW="1400px">
        <VStack spacing={8} align="stretch">
          {/* Cart Header */}
          <Box textAlign="center" mb={8}>
            <Heading size="2xl" color="purple.700" mb={4}>
              Shopping Cart
            </Heading>
            <Text fontSize="xl" color="purple.500">
              {cart.length} {cart.length === 1 ? "item" : "items"} in your cart
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
            {/* Cart Items */}
            <Box gridColumn={{ base: "1", lg: "span 2" }}>
              <VStack spacing={6} align="stretch">
                {cart.map((item: Product) => (
                  <MotionBox
                    key={item.id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <Box
                      bg={bgColor}
                      borderRadius="xl"
                      p={6}
                      shadow="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <HStack spacing={6} align="center">
                        <Box
                          position="relative"
                          width="120px"
                          height="120px"
                          flexShrink={0}
                        >
                          <Image
                            src={item.image}
                            alt={item.name}
                            borderRadius="lg"
                            objectFit="cover"
                            width="100%"
                            height="100%"
                          />
                          <Badge
                            position="absolute"
                            top={2}
                            right={2}
                            colorScheme="purple"
                            borderRadius="full"
                            px={2}
                            py={1}
                          >
                            {item.quantity}
                          </Badge>
                        </Box>
                        <VStack flex={1} align="start" spacing={2}>
                          <Heading size="md" color="purple.700">
                            {item.name}
                          </Heading>
                          <Text color="gray.600" fontSize="sm">
                            {item.description}
                          </Text>
                          <HStack spacing={4}>
                            <Text color="purple.600" fontWeight="bold" fontSize="lg">
                              Rs. {item.price.toLocaleString()}
                            </Text>
                            {item.originalPrice && (
                              <Text
                                color="gray.400"
                                textDecoration="line-through"
                                fontSize="sm"
                              >
                                Rs. {item.originalPrice.toLocaleString()}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                        <VStack spacing={4} align="end">
                          <NumberInput
                            min={1}
                            max={10}
                            value={item.quantity}
                            onChange={(value) =>
                              handleQuantityChange(item.id, parseInt(value))
                            }
                            size="sm"
                            width="100px"
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <IconButton
                            aria-label="Remove item"
                            icon={<FiTrash2 />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleRemove(item.id)}
                            size="sm"
                          />
                        </VStack>
                      </HStack>
                    </Box>
                  </MotionBox>
                ))}
              </VStack>
            </Box>

            {/* Order Summary */}
            <Box
              bg={bgColor}
              borderRadius="xl"
              p={6}
              shadow="md"
              borderWidth="1px"
              borderColor={borderColor}
              height="fit-content"
            >
              <Heading size="lg" color="purple.700" mb={6}>
                Order Summary
              </Heading>
              <VStack spacing={4} align="stretch">
                <Flex justify="space-between">
                  <Text color="gray.600">Subtotal</Text>
                  <Text color="purple.600" fontWeight="bold">
                    Rs. {calculateTotal().toLocaleString()}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text color="gray.600">Shipping</Text>
                  <Text color="green.500" fontWeight="bold">
                    Free
                  </Text>
                </Flex>
                <Divider />
                <Flex justify="space-between">
                  <Text fontSize="lg" fontWeight="bold" color="purple.700">
                    Total
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="purple.700">
                    Rs. {calculateTotal().toLocaleString()}
                  </Text>
                </Flex>
                <Button
                  colorScheme="purple"
                  size="lg"
                  mt={4}
                  onClick={() => navigate("/checkout")}
                >
                  Proceed to Checkout
                </Button>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  size="lg"
                  onClick={() => navigate("/categories")}
                >
                  Continue Shopping
                </Button>
              </VStack>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Cart;
